import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wallet } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { BudgetBar } from '@/features/projects/components/BudgetBar';
import { useProjectBudget, useProjects } from '@/features/projects';
import { formatMoney } from '@/lib/money';
import type { Project } from '@/features/projects/types';

function BudgetRow({ project }: { project: Project }) {
  const { data, isPending } = useProjectBudget(project.id);

  if (isPending || !data) {
    return (
      <TableRow>
        <TableCell colSpan={6}>
          <Skeleton className="h-6 w-full" />
        </TableCell>
      </TableRow>
    );
  }

  const budgetHours = Number(data.budget_hours);
  const actualHours = Number(data.actual_hours);
  const pct = budgetHours > 0 ? Math.round((actualHours / budgetHours) * 100) : null;
  const budgetMoney = Number(data.budget_amount);

  return (
    <TableRow>
      <TableCell className="font-medium">{project.name}</TableCell>
      <TableCell className="text-right tabular-nums">
        {budgetMoney > 0 ? formatMoney(data.budget_amount, data.currency) : '—'}
      </TableCell>
      <TableCell className="text-right tabular-nums">{budgetHours || '—'}</TableCell>
      <TableCell className="text-right tabular-nums">{actualHours.toFixed(2)}</TableCell>
      <TableCell className="text-right tabular-nums">
        {pct == null ? '—' : `${pct}%`}
      </TableCell>
      <TableCell>
        {budgetHours > 0 ? (
          <div className="flex items-center gap-2">
            <BudgetBar actual={actualHours} budget={budgetHours} />
            {data.over_budget ? (
              <Badge variant="destructive" className="shrink-0">
                Over
              </Badge>
            ) : null}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No budget set</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function BudgetingPage() {
  const { data, isPending, isError, error, isFetching } = useProjects({});
  const projects: Project[] = useMemo(() => data?.results ?? [], [data]);

  // Totals are by currency — project budgets are recorded in their own currency.
  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of projects) {
      const amount = Number(p.budget_amount) || 0;
      if (amount <= 0) continue;
      map.set(p.currency, (map.get(p.currency) ?? 0) + amount);
    }
    return [...map.entries()];
  }, [projects]);

  const withBudgets = projects.filter(
    (p) => Number(p.budget_hours) > 0 || Number(p.budget_amount) > 0,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Budgeting</h1>
          <p className="text-muted-foreground text-sm">
            Project budgets across the organization. Actuals come from logged time.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/finance/reports">
            P&amp;L &amp; Revenue Reports <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Projects with a budget</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {isPending ? '—' : withBudgets.length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="sm:col-span-2">
          <CardHeader>
            <CardDescription>Total budgeted (by currency)</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 pt-0">
            {totalsByCurrency.length ? (
              totalsByCurrency.map(([currency, total]) => (
                <Badge key={currency} variant="secondary" className="text-base tabular-nums">
                  {formatMoney(String(total), currency)}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground text-sm">No money budgets recorded.</span>
            )}
          </CardContent>
        </Card>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load budgets.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead className="text-right">Budget ($)</TableHead>
                <TableHead className="text-right">Budget (h)</TableHead>
                <TableHead className="text-right">Actual (h)</TableHead>
                <TableHead className="text-right">% Used</TableHead>
                <TableHead>Utilization</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : projects.length ? (
                projects.map((p) => <BudgetRow key={p.id} project={p} />)
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground text-center">
                    <Wallet className="text-muted-foreground mx-auto mb-2 size-6" />
                    No projects yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
